// Cached fetch helpers and per-route prefetchers.
//
// Every page-level GET request in the app should go through one of the
// cachedXxx() helpers below. Route loaders call prefetchXxx() to start
// the same fetches before the page component mounts, so the page's own
// calls find an in-flight (or resolved) promise in the cache and skip
// the network round trip.

import { cacheGet, invalidatePrefix } from './dataCache';
import { plaidService } from './plaidService';
import { analyticsService, AnalyticsParams, TimePeriod } from './analyticsService';

// Cache key prefixes — used for bulk invalidation after mutations or sync.
export const CACHE_PREFIX = {
  plaid: 'plaid:',
  analytics: 'analytics:',
} as const;

// ---------- Plaid cached fetchers ----------

export const cachedSpending2 = (page: number, pageSize: number) =>
  cacheGet(`plaid:spending2:${page}:${pageSize}`, () => plaidService.getSpending2(page, pageSize));

export const cachedIncome2 = (page: number, pageSize: number) =>
  cacheGet(`plaid:income2:${page}:${pageSize}`, () => plaidService.getIncome2(page, pageSize));

export const cachedNetWorthTrend = (period: string) =>
  cacheGet(`plaid:netWorthTrend:${period}`, () => plaidService.getNetWorthTrend(period));

export const cachedRecurring = () =>
  cacheGet('plaid:recurring', () => plaidService.getRecurring());

export const cachedNetIncomeLast3Months = () =>
  cacheGet('plaid:netIncomeLast3Months', () => plaidService.getNetIncomeLast3Months());

export const cachedManualAccounts = () =>
  cacheGet('plaid:manualAccounts', () => plaidService.getManualAccounts());

export const cachedAccounts = () =>
  cacheGet('plaid:accounts', () => plaidService.getAccounts());

export const cachedTransactions = (startDate?: string, endDate?: string) =>
  cacheGet(`plaid:transactions:${startDate || ''}:${endDate || ''}`, () =>
    plaidService.getTransactions(startDate, endDate),
  );

export const cachedSpending = (startDate?: string, endDate?: string) =>
  cacheGet(`plaid:spending:${startDate || ''}:${endDate || ''}`, () =>
    plaidService.getSpending(startDate, endDate),
  );

export const cachedIncome = (startDate?: string, endDate?: string) =>
  cacheGet(`plaid:income:${startDate || ''}:${endDate || ''}`, () =>
    plaidService.getIncome(startDate, endDate),
  );

export const cachedSpendingByCategory = (startDate: string, endDate: string) =>
  cacheGet(`plaid:spendingByCategory:${startDate}:${endDate}`, () =>
    plaidService.getSpendingByCategory(startDate, endDate),
  );

export const cachedMonthlySpend = () =>
  cacheGet('plaid:monthlySpend', () => plaidService.getMonthlySpend());

export const cachedYearlySalary = () =>
  cacheGet('plaid:yearlySalary', () => plaidService.getYearlySalary());

export const cachedLinkToken = () =>
  cacheGet('plaid:linkToken', () => plaidService.createLinkToken());

// ---------- Analytics cached fetchers ----------

const paramsKey = (params: AnalyticsParams) => JSON.stringify(params);

export const cachedAnalyticsByCategory = (params: AnalyticsParams) =>
  cacheGet(`analytics:byCategory:${paramsKey(params)}`, () => analyticsService.getByCategory(params));

export const cachedAnalyticsTopMerchants = (params: AnalyticsParams) =>
  cacheGet(`analytics:topMerchants:${paramsKey(params)}`, () => analyticsService.getTopMerchants(params));

export const cachedAnalyticsCreditBalances = () =>
  cacheGet('analytics:creditBalances', () => analyticsService.getCreditBalances().catch(() => null));

export const cachedAnalyticsSavings = () =>
  cacheGet('analytics:savings', () => analyticsService.getSavings().catch(() => null));

export const cachedAnalyticsAccountOverview = () =>
  cacheGet('analytics:accountOverview', () => analyticsService.getAccountOverview().catch(() => null));

export const cachedCreditReturns = () =>
  cacheGet('analytics:creditReturns', () => analyticsService.getCreditReturns());

// ---------- Cache invalidation helpers ----------

// Call after Plaid sync, manual-account mutations, or anything that
// changes the underlying account/transaction data on the server.
export const invalidateAllData = () => {
  invalidatePrefix(CACHE_PREFIX.plaid);
  invalidatePrefix(CACHE_PREFIX.analytics);
};

export const invalidateAccounts = () => {
  invalidatePrefix('plaid:accounts');
  invalidatePrefix('plaid:manualAccounts');
  invalidatePrefix('plaid:netWorthTrend');
  invalidatePrefix('analytics:accountOverview');
};

// ---------- Per-route prefetchers ----------
// Called by RoutePrefetch wrappers to start all needed fetches in parallel
// the moment a route begins mounting (before the page component renders).

const SPENDING_PAGE_SIZE = 25;
const INCOME_PAGE_SIZE = 25;

export const prefetchDashboard = () => {
  cachedSpending2(1, SPENDING_PAGE_SIZE);
  cachedIncome2(1, INCOME_PAGE_SIZE);
  cachedNetWorthTrend('3m');
  cachedRecurring();
  cachedCreditReturns();
  cachedNetIncomeLast3Months();
  cachedManualAccounts();

  // SpendingBreakdown defaults to the current month — prime that key too.
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  cachedSpendingByCategory(
    monthStart.toISOString().split('T')[0],
    monthEnd.toISOString().split('T')[0],
  );
};

export const prefetchAccounts = () => {
  cachedAccounts();
  cachedManualAccounts();
  cachedLinkToken();
};

export const prefetchTransactions = () => {
  cachedTransactions();
  cachedAccounts();
};

export const prefetchBudgets = () => {
  cachedMonthlySpend();
  cachedYearlySalary();

  // SpendingByCategoryChart fetches the last 3 calendar months — prime each.
  const now = new Date();
  for (let i = 2; i >= 0; i--) {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
    cachedSpendingByCategory(
      start.toISOString().split('T')[0],
      end.toISOString().split('T')[0],
    );
  }
};

const defaultAnalyticsPeriod: TimePeriod = 'month';
const getDefaultAnalyticsDateRange = (): { startDate: string; endDate: string } => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(endDate.getMonth() - 1);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

export const prefetchAnalytics = () => {
  const params: AnalyticsParams = { period: defaultAnalyticsPeriod };
  const { startDate, endDate } = getDefaultAnalyticsDateRange();
  cachedAnalyticsByCategory(params);
  cachedAnalyticsTopMerchants(params);
  cachedAnalyticsCreditBalances();
  cachedAnalyticsSavings();
  cachedAnalyticsAccountOverview();
  cachedSpending(startDate, endDate);
  cachedIncome(startDate, endDate);
};

export const prefetchProfile = () => {
  // Profile reads from AuthContext only — no API prefetch needed.
};
